/**
 * Query parser for search syntax
 * Supports: exact phrases, exclusions, and prefix matching
 * 
 * Syntax:
 * - `word1 word2` - both terms must match (AND)
 * - `"exact phrase"` - phrase must appear as-is
 * - `-excluded` - term must NOT appear
 * - `prefix*` - prefix matching (for autocomplete)
 */

import { tokenize } from './tokenizer';

export interface ParsedQuery {
  // Regular terms (all must match - AND logic)
  terms: string[];
  // Exact phrase matches (each phrase is array of consecutive tokens)
  phrases: string[][];
  // Terms to exclude (NOT logic)
  excluded: string[];
  // Prefix terms (for autocomplete)
  prefixes: string[];
  // Whether query is empty
  isEmpty: boolean;
}

/**
 * Parse a search query into structured components
 */
export function parseQuery(input: string): ParsedQuery {
  const result: ParsedQuery = {
    terms: [],
    phrases: [],
    excluded: [],
    prefixes: [],
    isEmpty: true,
  };

  if (!input || !input.trim()) {
    return result;
  }

  const query = input.trim();
  let remaining = query;

  // 1. Extract quoted phrases first
  const phraseRegex = /"([^"]+)"/g;
  let match: RegExpExecArray | null;

  while ((match = phraseRegex.exec(query)) !== null) {
    const phraseText = match[1];
    const phraseTokens = tokenize(phraseText);
    if (phraseTokens.length > 0) {
      result.phrases.push(phraseTokens);
      result.isEmpty = false;
    }
  }

  // Remove quoted phrases from remaining text
  remaining = remaining.replace(phraseRegex, ' ');

  // 2. Split remaining by whitespace and process each part
  const parts = remaining.split(/\s+/).filter(p => p.length > 0);

  for (const part of parts) {
    // Exclusion: starts with -
    if (part.startsWith('-') && part.length > 1) {
      const term = part.slice(1).toLowerCase();
      const tokens = tokenize(term);
      result.excluded.push(...tokens);
      result.isEmpty = false;
      continue;
    }

    // Prefix: ends with *
    if (part.endsWith('*') && part.length > 1) {
      const prefix = part.slice(0, -1).toLowerCase();
      if (prefix.length >= 2) {
        result.prefixes.push(prefix);
        result.isEmpty = false;
      }
      continue;
    }

    // Regular term
    const tokens = tokenize(part);
    for (const token of tokens) {
      result.terms.push(token);
      result.isEmpty = false;
    }
  }

  return result;
}

/**
 * Simple query parser - just tokenizes without special syntax
 * Used when you don't need phrase/exclusion/prefix support
 */
export function parseSimpleQuery(input: string): string[] {
  if (!input || !input.trim()) {
    return [];
  }
  return tokenize(input);
}

/**
 * Check if a document's tokens contain a phrase (consecutive tokens)
 */
export function containsPhrase(docTokens: string[], phrase: string[]): boolean {
  if (phrase.length === 0) return true;
  if (docTokens.length < phrase.length) return false;

  // Sliding window to find the phrase
  outer: for (let i = 0; i <= docTokens.length - phrase.length; i++) {
    for (let j = 0; j < phrase.length; j++) {
      if (docTokens[i + j] !== phrase[j]) {
        continue outer;
      }
    }
    return true;
  }

  return false;
}

/**
 * Highlight matched terms in text (for display)
 * Returns text with matched terms wrapped in markers
 */
export function highlightMatches(
  text: string,
  matchedTerms: string[],
  wrapStart: string = '<mark>',
  wrapEnd: string = '</mark>'
): string {
  if (!text || matchedTerms.length === 0) return text;

  let result = text;

  for (const term of matchedTerms) {
    // Case-insensitive replace with preserved original case
    const regex = new RegExp(`(${escapeRegex(term)})`, 'gi');
    result = result.replace(regex, `${wrapStart}$1${wrapEnd}`);
  }

  return result;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

